import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReplyCreditPacks } from "@/components/ReplyCreditPacks";
import { Button } from "@/components/ui/button";
import {
  Inbox, MailOpen, Clock, MessageSquare, Send, Archive, Search, Reply,
  Paperclip, Loader2, X, CheckCircle2, AlertCircle, FileText, MapPin,
  CalendarDays, LayoutList, Sparkles, Tag, Briefcase, Mail,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${fmtDate(iso)}, ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
}
function ago(iso) {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const LABEL_META = {
  replied: { label: "Replied", cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  awaiting: { label: "Awaiting", cls: "bg-amber-50 text-amber-600 border-amber-200" },
  "follow-up": { label: "Follow-up", cls: "bg-sky-50 text-sky-600 border-sky-200" },
};

function Logo({ t, size = "w-9 h-9" }) {
  const [failed, setFailed] = useState(false);
  const src = t.domain ? `https://www.google.com/s2/favicons?domain=${t.domain}&sz=64` : null;
  if (!src || failed) {
    return (
      <div className={`${size} rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 font-bold shrink-0`}>
        {(t.company || "?")[0].toUpperCase()}
      </div>
    );
  }
  return <img src={src} alt={t.company} onError={() => setFailed(true)}
    className={`${size} rounded-lg object-contain bg-white border border-slate-200 p-1 shrink-0`} />;
}

function MessageBubble({ m, candidateInitial }) {
  const outbound = m.direction === "outbound";
  return (
    <div data-testid={`comm-message-${m.direction}`} className="flex gap-3">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
        outbound ? "bg-amber-500 text-white" : "bg-violet-100 text-violet-700"
      }`}>{outbound ? candidateInitial : "R"}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{m.from_name}</span>
          <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border ${outbound ? "text-amber-700 bg-amber-50 border-amber-200" : "text-violet-700 bg-violet-50 border-violet-200"}`}>
            {outbound ? "You" : "Recruiter"}
          </span>
          <span className="text-[11px] text-slate-400 ml-auto whitespace-nowrap">{fmtDateTime(m.at)}</span>
        </div>
        <div className={`mt-1.5 rounded-xl px-4 py-3 border ${outbound ? "bg-slate-50 border-slate-100" : "bg-violet-50/40 border-violet-100"}`}>
          {m.subject && <p className="text-sm font-semibold text-slate-800 mb-1">{m.subject}</p>}
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{m.body || "(No message content)"}</p>
          {m.attachments?.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-2">
              {m.attachments.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600">
                  <Paperclip className="w-3.5 h-3.5 text-slate-400" /> {a.filename}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepDot({ n, label, sub, active, done }) {
  return (
    <div className="flex items-center gap-2.5 shrink-0">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        active ? "bg-amber-500 text-white" : done ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-400"
      }`}>{done ? <CheckCircle2 className="w-4 h-4" /> : n}</div>
      <div className="hidden md:block">
        <p className={`text-sm font-semibold ${active || done ? "text-slate-900" : "text-slate-400"}`}>{label}</p>
        <p className="text-[11px] text-slate-400">{sub}</p>
      </div>
    </div>
  );
}

function ReplyModal({ thread, onClose, onSent }) {
  const { refreshUser } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [catKey, setCatKey] = useState(null);
  const [subKey, setSubKey] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [missing, setMissing] = useState([]);
  const [balance, setBalance] = useState(null);
  const [quotaObj, setQuotaObj] = useState(null);
  const [showPacks, setShowPacks] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const noRecipient = !thread.recruiter_id;

  const fetchQuota = useCallback(() =>
    api.get("/communications/quota").then(({ data }) => {
      setQuotaObj(data);
      setBalance(data.unlimited ? "Unlimited" : `${data.remaining} left`);
    }).catch(() => {}), []);

  useEffect(() => {
    fetchQuota();
    api.get(`/communications/${thread.id}/reply-context`).then(({ data }) => {
      setCtx(data);
      const access = data.access;
      const initialCat = access.category_mode === "fixed"
        ? access.fixed_category?.key
        : access.categories[0]?.key;
      setCatKey(initialCat);
    }).catch(() => toast.error("Could not load reply options."));
  }, [thread.id]);

  const categories = ctx?.access?.categories || [];
  const activeCat = categories.find((c) => c.key === catKey) || categories[0];

  useEffect(() => {
    if (!activeCat) return;
    const first = activeCat.subcategories.find((s) => s.template_count > 0) || activeCat.subcategories[0];
    setSubKey(first?.key || null);
  }, [catKey, ctx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!subKey) { setTemplates([]); setTemplateId(null); return; }
    api.get(`/communications/${thread.id}/reply-templates`, { params: { subcategory: subKey } })
      .then(({ data }) => {
        setTemplates(data.items);
        setTemplateId(data.items[0]?.id || null);
      }).catch(() => { setTemplates([]); setTemplateId(null); });
  }, [subKey, thread.id]);

  useEffect(() => {
    if (!templateId) { setPreview(null); setMissing([]); return; }
    setLoadingPreview(true);
    api.post(`/communications/${thread.id}/reply/preview`, { template_id: templateId })
      .then(({ data }) => { setPreview(data); setMissing(data.missing_fields || []); })
      .catch(() => { setPreview(null); setMissing([]); })
      .finally(() => setLoadingPreview(false));
  }, [templateId, thread.id]);

  const outOfCredits = !!quotaObj && !quotaObj.unlimited && (quotaObj.remaining ?? 0) <= 0;
  const canSend = !!templateId && !missing.length && !loadingPreview && !sending && !!preview && !noRecipient && !outOfCredits;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await api.post(`/communications/${thread.id}/reply`, { template_id: templateId });
      await refreshUser();
      fetchQuota();
      toast.success(`Reply sent to ${thread.company}.`);
      onSent();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        setShowPacks(true);
        fetchQuota();
        toast.error(typeof detail === "string" ? detail : "You're out of reply credits. Buy a top-up pack to keep replying.");
      }
      else if (e.response?.status === 403) toast.error(typeof detail === "string" ? detail : "This template is locked at the current stage.");
      else if (detail?.error === "missing_fields") setMissing(detail.fields || []);
      else toast.error(formatApiError(detail));
    } finally {
      setSending(false);
    }
  };

  const selectMode = ctx?.access?.category_mode === "select";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-3 sm:p-6" data-testid="reply-modal">
      <div className="bg-white rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[94vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-display">Choose Your Reply</h2>
            <p className="text-sm text-slate-500">Select a suitable reply template for this job communication.</p>
          </div>
          <button onClick={onClose} data-testid="reply-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        {!ctx ? (
          <div className="py-24 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" /></div>
        ) : (
          <div className="overflow-y-auto">
            {/* Scenario / Category bar */}
            <div className="px-6 pt-5">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3">
                  <Briefcase className="w-5 h-5 text-slate-400" />
                  <div><p className="text-sm font-semibold text-slate-900">{ctx.job_title}</p><p className="text-xs text-slate-500">{ctx.company}</p></div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div><p className="text-[11px] text-slate-400">Scenario</p><p className="text-sm font-semibold text-slate-900" data-testid="reply-scenario">{ctx.access.stage_label}</p></div>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-center gap-3">
                  <Tag className="w-5 h-5 text-amber-500" />
                  <div><p className="text-[11px] text-amber-600">Category</p>
                    <p className="text-sm font-semibold text-slate-900" data-testid="reply-category">
                      {selectMode ? (activeCat?.name || "Select") : `${ctx.access.fixed_category?.name} (Fixed)`}
                    </p></div>
                </div>
              </div>

              {/* Stepper */}
              <div className="flex items-center justify-between gap-2 mt-5 overflow-x-auto pb-1">
                <StepDot n={1} label="Scenario" sub={ctx.access.stage_label} done active={false} />
                <div className="h-px flex-1 bg-slate-200 min-w-[16px]" />
                <StepDot n={2} label="Subcategory" sub="Choose intent" active={!templateId} done={!!templateId} />
                <div className="h-px flex-1 bg-slate-200 min-w-[16px]" />
                <StepDot n={3} label="Template" sub="Select a template" active={!!templateId && !preview} done={!!preview} />
                <div className="h-px flex-1 bg-slate-200 min-w-[16px]" />
                <StepDot n={4} label="Preview" sub="Review and send" active={!!preview} done={false} />
              </div>
            </div>

            {noRecipient && (
              <div className="mx-6 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex gap-2.5" data-testid="reply-no-recipient">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /> No recruiter contact is on record for this application, so a reply can't be delivered.
              </div>
            )}

            {/* Category selector (only when stage allows multiple) */}
            {selectMode && (
              <div className="px-6 mt-5">
                <p className="text-sm font-semibold text-slate-900 mb-2">Choose a category</p>
                <div className="flex flex-wrap gap-2" data-testid="reply-category-picker">
                  {categories.map((c) => (
                    <button key={c.key} data-testid={`reply-category-${c.key}`} onClick={() => setCatKey(c.key)}
                      className={`px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                        c.key === catKey ? "bg-amber-500 text-black border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"
                      }`}>{c.name}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-[1fr_1fr] gap-5 px-6 py-5">
              {/* Left: subcategory + template */}
              <div className="space-y-5">
                <div>
                  <p className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">1</span> Choose a subcategory</p>
                  <div className="grid grid-cols-2 gap-2.5" data-testid="reply-subcategory-grid">
                    {(activeCat?.subcategories || []).map((s) => (
                      <button key={s.key} data-testid={`reply-subcategory-${s.key}`} onClick={() => setSubKey(s.key)}
                        disabled={s.template_count === 0}
                        className={`text-left rounded-xl border px-3.5 py-3 transition-colors disabled:opacity-40 ${
                          s.key === subKey ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-300" : "border-slate-200 bg-white hover:border-amber-200"
                        }`}>
                        <div className="flex items-center justify-between">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className={`w-4 h-4 rounded-full border-2 ${s.key === subKey ? "border-amber-500 bg-amber-500" : "border-slate-300"}`} />
                        </div>
                        <p className="text-sm font-semibold text-slate-900 mt-2">{s.name}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{s.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">2</span> Select a template</p>
                  <div className="space-y-2" data-testid="reply-template-list">
                    {templates.length === 0 ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">No templates in this subcategory yet.</div>
                    ) : templates.map((t) => (
                      <button key={t.id} data-testid={`reply-template-${t.id}`} onClick={() => setTemplateId(t.id)}
                        className={`w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                          t.id === templateId ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-300" : "border-slate-200 bg-white hover:border-amber-200"
                        }`}>
                        <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${t.id === templateId ? "border-amber-500 bg-amber-500" : "border-slate-300"}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate">{t.name}</p>
                          <p className="text-xs text-slate-500 truncate">Subject: {t.subject_preview}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: email preview */}
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">3</span> Email Preview</p>
                {loadingPreview ? (
                  <div className="rounded-xl border border-slate-200 py-16 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-amber-500" /></div>
                ) : missing.length > 0 ? (
                  <div data-testid="reply-missing-error" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 flex gap-2.5">
                    <AlertCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-rose-700">Required fields missing: {missing.map((m) => m.label).join(", ")}.</p>
                      <p className="text-xs text-rose-600 mt-1">Complete these in your profile or pick another template.</p>
                    </div>
                  </div>
                ) : preview ? (
                  <div data-testid="reply-preview" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <p className="text-sm font-semibold text-slate-800">Subject: {preview.subject}</p>
                    </div>
                    <div className="px-4 py-4 text-sm text-slate-700 whitespace-pre-wrap break-words max-h-72 overflow-y-auto">{preview.body}</div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center text-sm text-slate-400">Select a template to preview.</div>
                )}
                <p className="mt-2 text-[11px] text-slate-400">Predefined templates only — no free-text editing. Merge fields are filled from your profile and this application.</p>
              </div>
            </div>
          </div>
        )}

        {(showPacks || outOfCredits) && (
          <div className="px-6 pt-4" data-testid="reply-buy-prompt">
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <p className="text-sm font-semibold text-slate-800 mb-2.5">You're out of reply credits — top up to keep replying</p>
              <ReplyCreditPacks onPurchased={() => { setShowPacks(false); fetchQuota(); }} />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-100">
          <span className="text-sm text-slate-500">Reply credits · <span className="font-semibold text-sky-600" data-testid="reply-credit-balance">{balance ?? "—"}</span>
            {quotaObj?.extra_credits > 0 && <span className="text-xs text-slate-400 ml-1.5" data-testid="reply-credit-purchased">(incl. {quotaObj.extra_credits} purchased)</span>}
          </span>
          <div className="ml-auto flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} data-testid="reply-cancel" className="text-slate-600">Cancel</Button>
            <Button data-testid="reply-send" onClick={send} disabled={!canSend}
              className="bg-amber-500 text-black hover:bg-amber-400 font-semibold px-8">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Use This Reply</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const FOLDERS = [
  { key: "inbox", label: "Inbox", Icon: Inbox, count: "total_threads" },
  { key: "unread", label: "Unread", Icon: MailOpen, count: "unread" },
  { key: "replied", label: "Replied", Icon: MessageSquare, count: "replied" },
  { key: "awaiting", label: "Awaiting Response", Icon: Clock, count: "awaiting_response" },
  { key: "follow-ups", label: "Follow-ups", Icon: Send },
  { key: "archived", label: "Archived", Icon: Archive },
  { key: "templates", label: "Templates", Icon: LayoutList },
];

export default function Communications() {
  const [params, setParams] = useSearchParams();
  const [folder, setFolder] = useState("inbox");
  const [summary, setSummary] = useState({ total_threads: 0, unread: 0, awaiting_response: 0, replied: 0 });
  const [threads, setThreads] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", company: "", role: "", status: "", recruiter: "", period: "all" });
  const [options, setOptions] = useState({ companies: [], roles: [], recruiters: [] });
  const [replyTemplates, setReplyTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const pollRef = useRef(null);
  const initFromUrl = useRef(true);

  const loadSummary = useCallback(() => {
    api.get("/communications/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  const loadThreads = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    api.get("/communications", { params: { folder, ...filters, page: 1, page_size: 50 } })
      .then(({ data }) => setThreads(data))
      .catch(() => { if (!silent) toast.error("Could not load conversations."); })
      .finally(() => setLoading(false));
  }, [folder, filters]);

  useEffect(() => {
    api.get("/communications/filters").then(({ data }) => setOptions(data)).catch(() => {});
    api.get("/reply-templates").then(({ data }) => setReplyTemplates(data.items)).catch(() => {});
  }, []);

  useEffect(() => { loadThreads(); loadSummary(); }, [loadThreads, loadSummary]);
  useEffect(() => {
    pollRef.current = setInterval(() => { loadThreads(true); loadSummary(); }, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadThreads, loadSummary]);

  // Auto-select first thread, honoring deep-link ?app=
  useEffect(() => {
    if (folder === "templates") { setSelectedId(null); return; }
    if (initFromUrl.current) {
      const appParam = params.get("app");
      if (appParam) { setSelectedId(appParam); initFromUrl.current = false; return; }
      initFromUrl.current = false;
    }
    if (threads.items.length && !threads.items.find((t) => t.id === selectedId)) {
      setSelectedId(threads.items[0].id);
    }
    if (!threads.items.length && !params.get("app")) setSelectedId(null);
  }, [threads, folder]); // eslint-disable-line react-hooks/exhaustive-deps

  const openThread = useCallback((id, autoReply = false) => {
    if (!id) { setThread(null); return; }
    setThreadLoading(true);
    api.get(`/communications/${id}`).then(({ data }) => {
      setThread(data);
      if (autoReply) setReplyOpen(true);
      if (data.unread > 0) {
        api.post(`/communications/${id}/read`).then(() => {
          window.dispatchEvent(new Event("replies-updated"));
          loadThreads(true); loadSummary();
        }).catch(() => {});
      }
    }).catch(() => setThread(null)).finally(() => setThreadLoading(false));
  }, [loadThreads, loadSummary]);

  useEffect(() => {
    if (!selectedId) { setThread(null); return; }
    const autoReply = params.get("reply") === "1";
    openThread(selectedId, autoReply);
    if (autoReply) { params.delete("reply"); setParams(params, { replace: true }); }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doArchive = async () => {
    try {
      const { data } = await api.post(`/communications/${selectedId}/archive`);
      toast.success(data.archived ? "Conversation archived." : "Conversation restored.");
      loadThreads(true); loadSummary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onReplySent = () => {
    setReplyOpen(false);
    openThread(selectedId);
    loadThreads(true); loadSummary();
  };

  const clearFilters = () => setFilters({ search: filters.search, company: "", role: "", status: "", recruiter: "", period: "all" });
  const hasFilters = filters.company || filters.role || filters.status || filters.recruiter || filters.period !== "all";

  const STAT_CARDS = [
    { label: "Total Threads", value: summary.total_threads, Icon: Inbox, cls: "bg-sky-50 text-sky-500" },
    { label: "Unread", value: summary.unread, Icon: MailOpen, cls: "bg-violet-50 text-violet-500" },
    { label: "Awaiting Response", value: summary.awaiting_response, Icon: Clock, cls: "bg-amber-50 text-amber-500" },
  ];
  const selectCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600 outline-none focus:border-amber-500";
  const candidateInitial = (thread?.candidate_name || "Y")[0].toUpperCase();

  return (
    <div data-testid="communications-page" className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-r from-amber-50 via-white to-amber-100/60 border border-amber-100 px-6 py-7">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Communications</h1>
        <p className="text-slate-500 mt-1.5">Manage recruiter conversations and replies in one dedicated workspace.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {STAT_CARDS.map((c) => (
          <div key={c.label} data-testid={`comm-stat-${c.label.split(" ")[0].toLowerCase()}`}
            className="rounded-2xl border border-slate-200 bg-white px-5 py-4 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl ${c.cls} flex items-center justify-center`}><c.Icon className="w-5 h-5" /></div>
            <div><p className="text-xs text-slate-500">{c.label}</p><p className="text-2xl font-bold text-slate-900">{c.value}</p></div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[220px_1fr_440px] gap-5 items-start">
        {/* Folders + filters */}
        <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-1 lg:sticky lg:top-24" data-testid="comm-folders">
          <p className="px-2 pt-1 pb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase">Folders</p>
          {FOLDERS.map((f) => {
            const active = folder === f.key;
            const count = f.count ? summary[f.count] : null;
            return (
              <button key={f.key} data-testid={`comm-folder-${f.key}`} onClick={() => setFolder(f.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active ? "bg-amber-50 text-amber-600" : "text-slate-600 hover:bg-slate-100"
                }`}>
                <f.Icon className="w-4 h-4 shrink-0" /> <span className="flex-1 text-left">{f.label}</span>
                {count ? <span className={`text-xs font-bold ${active ? "text-amber-600" : "text-slate-400"}`}>{count}</span> : null}
              </button>
            );
          })}

          <div className="pt-3 mt-2 border-t border-slate-100 space-y-2.5">
            <p className="px-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase">Filters</p>
            <select data-testid="filter-company" value={filters.company} onChange={(e) => setFilters({ ...filters, company: e.target.value })} className={selectCls}>
              <option value="">All Companies</option>
              {options.companies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="filter-role" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} className={selectCls}>
              <option value="">All Roles</option>
              {options.roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={selectCls}>
              <option value="">All Statuses</option>
              <option value="delivered">Delivered</option>
              <option value="opened">Opened</option>
              <option value="replied">Replied</option>
              <option value="awaiting">Awaiting</option>
            </select>
            <select data-testid="filter-recruiter" value={filters.recruiter} onChange={(e) => setFilters({ ...filters, recruiter: e.target.value })} className={selectCls}>
              <option value="">All Recruiters</option>
              {options.recruiters.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="filter-date" value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.target.value })} className={selectCls}>
              <option value="all">Any Time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            {hasFilters && (
              <button data-testid="clear-filters" onClick={clearFilters} className="text-xs font-semibold text-amber-600 hover:text-amber-700 px-2">Clear filters</button>
            )}
          </div>
        </div>

        {/* Conversation list OR templates view */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="comm-list">
          {folder === "templates" ? (
            <div>
              <div className="px-5 py-4 border-b border-slate-100"><h2 className="font-bold text-slate-900">Reply Templates</h2>
                <p className="text-sm text-slate-500 mt-0.5">Predefined replies available when responding to recruiters.</p></div>
              <div className="divide-y divide-slate-50">
                {replyTemplates.length === 0 ? (
                  <p className="px-5 py-10 text-center text-sm text-slate-400">No reply templates configured yet.</p>
                ) : replyTemplates.map((t) => (
                  <div key={t.id} data-testid={`reply-template-item-${t.id}`} className="px-5 py-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0"><Sparkles className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-900">{t.name}</p><p className="text-xs text-slate-500">{t.category}</p></div>
                    {t.is_default && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">Recommended</span>}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <h2 className="font-bold text-slate-900 mr-auto">Conversations ({threads.total})</h2>
                </div>
                <div className="relative mt-3">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="comm-search" value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Search companies, roles, or subjects…"
                    className="h-10 w-full pl-9 pr-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-500" />
                </div>
              </div>

              <div className="divide-y divide-slate-50 max-h-[calc(100vh-16rem)] overflow-y-auto">
                {loading ? (
                  <div className="py-16 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" /></div>
                ) : threads.items.length === 0 ? (
                  <div className="py-16 text-center text-slate-500">
                    <Inbox className="w-9 h-9 mx-auto mb-2 text-slate-300" />
                    <p className="font-medium text-slate-700">No conversations here</p>
                    <p className="text-sm mt-1">Threads appear once you apply to a job.</p>
                  </div>
                ) : threads.items.map((t) => (
                  <button key={t.id} data-testid={`comm-thread-${t.id}`} onClick={() => setSelectedId(t.id)}
                    className={`w-full flex items-start gap-3 px-5 py-4 text-left transition-colors ${
                      selectedId === t.id ? "bg-amber-50/60 border-l-4 border-l-amber-500" : "hover:bg-slate-50 border-l-4 border-l-transparent"
                    }`}>
                    <Logo t={t} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900 truncate">{t.company}</p>
                        <span className="text-[11px] text-slate-400 ml-auto whitespace-nowrap">{ago(t.last_activity_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{t.job_title}</p>
                      <p className="text-xs text-slate-500 truncate mt-1">{t.last_message_preview || "—"}</p>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {t.labels.map((l) => LABEL_META[l] && (
                          <span key={l} className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border ${LABEL_META[l].cls}`}>{LABEL_META[l].label}</span>
                        ))}
                        {t.unread > 0 && (
                          <span data-testid={`comm-unread-${t.id}`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold ml-auto">{t.unread}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Thread detail */}
        <div className="rounded-2xl border border-slate-200 bg-white lg:sticky lg:top-24 flex flex-col max-h-[calc(100vh-7rem)]" data-testid="comm-detail">
          {folder === "templates" ? (
            <div className="py-20 text-center text-slate-500"><LayoutList className="w-9 h-9 mx-auto mb-2 text-slate-300" /><p className="font-medium text-slate-700">Reply templates</p><p className="text-sm mt-1">Select a conversation folder to reply.</p></div>
          ) : !selectedId || (!thread && !threadLoading) ? (
            <div className="py-20 text-center text-slate-500"><MessageSquare className="w-9 h-9 mx-auto mb-2 text-slate-300" /><p className="font-medium text-slate-700">Select a conversation</p></div>
          ) : threadLoading || !thread ? (
            <div className="py-20 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-amber-500" /></div>
          ) : (
            <>
              <div className="px-6 pt-5 pb-4 border-b border-slate-100">
                <div className="flex items-start gap-3">
                  <Logo t={thread} size="w-11 h-11" />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-900 leading-snug truncate" data-testid="comm-detail-company">{thread.company}</p>
                    <p className="text-sm text-slate-600 truncate">{thread.job_title}</p>
                  </div>
                  {thread.reply_status === "replied" && <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1 shrink-0">Replied</span>}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {thread.recruiter_name}</span>
                  {thread.job_location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {thread.job_location}</span>}
                  <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Applied {fmtDate(thread.applied_at)}</span>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button data-testid="comm-reply-btn" onClick={() => setReplyOpen(true)} className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">
                    <Reply className="w-4 h-4 mr-1.5" /> Reply
                  </Button>
                  <Button data-testid="comm-archive-btn" variant="outline" onClick={doArchive} className="border-slate-200 text-slate-600">
                    <Archive className="w-4 h-4 mr-1.5" /> {thread.archived ? "Restore" : "Archive"}
                  </Button>
                  {thread.template_name && <span className="ml-auto text-xs text-slate-400 inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {thread.template_name}</span>}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5" data-testid="comm-conversation">
                {(thread.messages || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No messages yet.</p>
                ) : thread.messages.map((m) => <MessageBubble key={m.message_id} m={m} candidateInitial={candidateInitial} />)}
              </div>

              <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-2.5 text-xs text-slate-500">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                Recruiter address kept private — respond using predefined reply templates only.
              </div>
            </>
          )}
        </div>
      </div>

      {replyOpen && thread && <ReplyModal thread={thread} onClose={() => setReplyOpen(false)} onSent={onReplySent} />}
    </div>
  );
}
